import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import {
  SharedSessionStore,
  cookieDomainFor,
  migrateLocalSession,
  probeCookies,
  watchShared,
  type CookieDoc,
  type KeyStore,
} from './auth/sharedSession.ts';
import { readConfig } from './config.ts';

let client: SupabaseClient | null = null;

/**
 * The client's sign-in settings (decision 801, docs/specs/cloud/sign-in-security.md §2). PKCE: every sign-in this
 * browser starts (email link, Microsoft, Google, LinkedIn) comes back as a one-time `?code=` that only this browser's
 * code verifier can exchange, so no token ever travels in an address. The flow id travels with the return, so two
 * sign-ins started at once (two tabs, or an email link and then Microsoft) each keep their own verifier; the
 * redirect list's `https://<portal>/**` entries allow the extra `?sb_flow_id=`. supabase-js never takes tokens from
 * the address fragment itself: an invitation's return is finished by `finishAuthReturn` (auth/authReturn.ts).
 *
 * Where the session is kept (decision 978, docs/specs/cloud/shared-sign-in.md): on an `https` address under the
 * build's cookie domain (`VITE_AUTH_COOKIE_DOMAIN`, production `cloud.teslyn.au`) it is a cookie for that domain, shared by
 * the front door and every company address under it, so a person signs in once (auth/sharedSession.ts). Anywhere
 * else it stays in this site's localStorage. Either way the page's own script can read it (supabase-js running in
 * the page needs the refresh token); an httpOnly cookie would need a server of Teslyn's own between the page and
 * Supabase (sign-in-security.md §2.2). What protects the stored session is the Content Security Policy
 * (security-headers.ts): only Teslyn Cloud's own scripts run, and they can only talk to Supabase.
 */
export const AUTH_OPTIONS = {
  flowType: 'pkce',
  persistSession: true,
  autoRefreshToken: true,
  // a `?code=` with this browser's verifier is exchanged; an address fragment is never read by supabase-js
  detectSessionInUrl: (): boolean => false,
  experimental: { appendPkceFlowIdToRedirects: true },
} as const;

/** supabase-js's own storage key for a project (`sb-<first label of the host>-auth-token`), kept as it was. */
export function sessionKeyFor(supabaseUrl: string): string {
  return `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;
}

/** Where this page keeps the session: the shared cookie for a domain, or this site's own storage. */
export type SessionPlace = { readonly kind: 'shared'; readonly domain: string } | { readonly kind: 'site' };

type PlaceWin = {
  readonly location: Pick<Location, 'hostname' | 'protocol'>;
  readonly document: CookieDoc;
};

/**
 * The shared cookie when this `https` address is under the build's cookie domain and the browser keeps a cookie for
 * it (cookies may be blocked); otherwise this site's storage, as before decision 978.
 */
export function choosePlace(win: PlaceWin, configured: string | undefined): SessionPlace {
  const domain = cookieDomainFor(win.location.hostname, win.location.protocol, configured);
  return domain && probeCookies(win.document, domain) ? { kind: 'shared', domain } : { kind: 'site' };
}

let place: SessionPlace | null = null;

/** This page's session place, decided once. */
export function sessionPlace(): SessionPlace {
  if (!place) place = choosePlace(window, readConfig().authCookieDomain);
  return place;
}

function siteStorage(): KeyStore | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * The session store for a shared place: the session moves once from this site's localStorage, and the page then
 * follows sign-ins and sign-outs made on the other addresses (`watchShared`).
 */
export function sharedStore(
  domain: string,
  sessionKey: string,
  doc: CookieDoc = document,
  local: KeyStore | null = siteStorage(),
): SharedSessionStore {
  const store = new SharedSessionStore({ doc, local, domain, sessionKey });
  migrateLocalSession(store, local);
  store.lastSeen = store.readRaw();
  return store;
}

/** The one Supabase client, created with the publishable key. RLS decides what every query returns. */
export function supabase(): SupabaseClient {
  if (!client) {
    const cfg = readConfig();
    if (!cfg.supabaseUrl || !cfg.supabasePublishableKey) {
      throw new Error(
        'Missing VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY; check apps/web/.env.<mode>',
      );
    }
    const where = sessionPlace();
    const storageKey = sessionKeyFor(cfg.supabaseUrl);
    const store = where.kind === 'shared' ? sharedStore(where.domain, storageKey) : null;
    client = createClient(cfg.supabaseUrl, cfg.supabasePublishableKey, {
      auth: { ...AUTH_OPTIONS, storageKey, ...(store ? { storage: store } : {}) },
    });
    if (store) watchShared(store, client);
  }
  return client;
}

/**
 * A second client that keeps nothing: no storage, no refresh, nothing read from the address. `finishAuthReturn` uses
 * it to find out whose email link it was handed while this browser is already signed in (security review M3), so the
 * link can never replace that sign-in.
 */
export function asideClient(): SupabaseClient {
  const cfg = readConfig();
  return createClient(cfg.supabaseUrl, cfg.supabasePublishableKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: 'teslyn-aside',
    },
  });
}
